import React, { createContext, useContext, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const FONT_OPTIONS = [
  { name: 'Syne', stack: "'Syne', sans-serif", href: 'https://fonts.googleapis.com/css2?family=Syne:wght@400;500;600;700;800&display=swap' },
  { name: 'Poppins', stack: "'Poppins', sans-serif", href: 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap' },
  { name: 'Montserrat', stack: "'Montserrat', sans-serif", href: 'https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap' },
  { name: 'Playfair Display', stack: "'Playfair Display', serif", href: 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;500;600;700;800&display=swap' },
  { name: 'Oswald', stack: "'Oswald', sans-serif", href: 'https://fonts.googleapis.com/css2?family=Oswald:wght@400;500;600;700&display=swap' },
  { name: 'Bebas Neue', stack: "'Bebas Neue', sans-serif", href: 'https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap' },
  { name: 'Raleway', stack: "'Raleway', sans-serif", href: 'https://fonts.googleapis.com/css2?family=Raleway:wght@400;500;600;700;800&display=swap' },
  { name: 'Inter', stack: "'Inter', sans-serif", href: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap' },
];

const DEFAULTS = {
  site_name: 'STUDIO AT THE THIRD',
  logo_url: null,
  hero_bg_url: '',
  hero_image_url: null,
  hero_title: 'STUDIO AT THE THIRD',
  hero_subtitle: 'Where Movement Meets Artistry',
  accent_color: '#FFD700',
  heading_font: 'Syne',
  hero_image_scale: 'medium',
  hero_subtitle_scale: 'medium',
  home_classes_desc: 'Join our diverse range of dance classes led by experienced instructors. From contemporary to hip hop, find your rhythm.',
  home_studio_desc: 'Book our professional studio, designed for rehearsals, practice, workshops, and performances.',
  home_community_desc: 'Join a vibrant community of dancers and artists. Connect, collaborate, and create together in our inclusive space.',
};

const clamp = (v) => Math.max(0, Math.min(255, Math.round(v)));

const hexToRgb = (hex) => {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex || '');
  if (!m) return { r: 255, g: 215, b: 0 };
  return { r: parseInt(m[1], 16), g: parseInt(m[2], 16), b: parseInt(m[3], 16) };
};

const darken = (hex, amount) => {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  return `#${[clamp(r * f), clamp(g * f), clamp(b * f)].map((x) => x.toString(16).padStart(2, '0')).join('')}`;
};

const rgba = (hex, alpha) => {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const applyTheme = (settings) => {
  const root = document.documentElement;
  const accent = settings.accent_color || DEFAULTS.accent_color;
  root.style.setProperty('--accent', accent);
  root.style.setProperty('--accent-hover', darken(accent, 0.12));
  root.style.setProperty('--accent-soft', rgba(accent, 0.1));

  const font = FONT_OPTIONS.find((f) => f.name === settings.heading_font) || FONT_OPTIONS[0];
  root.style.setProperty('--heading-font', font.stack);

  let link = document.getElementById('dynamic-heading-font');
  if (!link) {
    link = document.createElement('link');
    link.id = 'dynamic-heading-font';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  link.href = font.href;
};

const SiteSettingsContext = createContext({ settings: DEFAULTS, refresh: () => {} });

export const useSiteSettings = () => useContext(SiteSettingsContext);

const CACHE_KEY = 'site_settings_cache';

const loadCached = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch (e) { /* ignore */ }
  return DEFAULTS;
};

export const SiteSettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState(loadCached);

  // Apply theme synchronously BEFORE the browser paints, so the correct
  // fonts/accent/background are used on first render (no flash of defaults).
  useLayoutEffect(() => {
    applyTheme(settings);
  }, [settings]);

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/site-settings`);
      const merged = { ...DEFAULTS, ...res.data };
      setSettings(merged);
      try { localStorage.setItem(CACHE_KEY, JSON.stringify(res.data)); } catch (e) { /* quota */ }
    } catch (error) {
      console.error('Failed to load site settings:', error);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <SiteSettingsContext.Provider value={{ settings, refresh }}>
      {children}
    </SiteSettingsContext.Provider>
  );
};
